import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

// The quiet door. Not linked from anywhere on the site — you hand this URL to
// someone. Signing in here is what marks the account as an influencer, so the
// link itself is the invitation.
export default function InfluencerPortalPage() {
  const { signIn, signUp } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [channel, setChannel] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const claim = async () => {
    const { error: fnErr } = await supabase.functions.invoke('shinyvault-influencer', {
      body: { action: 'claim', channel: channel.trim() || undefined },
    });
    if (fnErr) throw new Error('Signed in, but the portal could not finish setting you up.');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setNotice(''); setBusy(true);
    try {
      if (mode === 'up') {
        const { error: upErr } = await signUp(email, password);
        if (upErr) throw upErr;
        // Email confirmation may be on, in which case there is no session yet.
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setNotice('Account created. Check your email to confirm, then come back to this link.');
          setMode('in'); return;
        }
      } else {
        const { error: inErr } = await signIn(email, password);
        if (inErr) throw inErr;
      }
      await claim();
      nav('/influencer');
    } catch (err) {
      setError(err.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sv-container" style={{ maxWidth: 460, padding: '60px 0 90px' }}>
      <div style={{ textAlign: 'center', marginBottom: 26 }}>
        <Sparkles size={26} style={{ color: 'var(--accent)' }} />
        <h1 style={{ fontSize: '1.5rem', fontWeight: 900, margin: '10px 0 6px' }}>Creator planning</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.6 }}>
          Tell us what you will need for upcoming content and we will source toward it.
          Nothing here is an order and nothing is priced yet.
        </p>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{error}</div>}
        {notice && <div style={{ color: 'var(--success)', fontSize: '0.85rem' }}>{notice}</div>}

        <input className="sv-input" type="email" placeholder="Email" value={email} required
               autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
        <input className="sv-input" type="password" placeholder="Password" value={password} required
               minLength={6} autoComplete={mode === 'up' ? 'new-password' : 'current-password'}
               onChange={(e) => setPassword(e.target.value)} />
        {mode === 'up' && (
          <input className="sv-input" placeholder="Channel or handle (optional)" value={channel}
                 onChange={(e) => setChannel(e.target.value)} />
        )}

        <button className="sv-btn" type="submit" disabled={busy}>
          {busy ? 'One moment' : mode === 'up' ? 'Create my account' : 'Sign in'}
        </button>

        <button type="button" onClick={() => setMode(mode === 'up' ? 'in' : 'up')}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-muted)',
                  fontSize: '0.85rem', cursor: 'pointer', padding: '6px 0',
                }}>
          {mode === 'up' ? 'I already have an account' : 'New here? Create an account'}
        </button>
      </form>
    </div>
  );
}
